import React from 'react'
import { Box, Flex, Text } from '@chakra-ui/react'

import {
    ChatFieldsFragment,
    PatientFieldsFragment,
} from '~graphql/generated/graphql'
import SpeciesIcon from '~components/shared/patient/species-icon'
import SharedStack from '~components/shared/wrappers/shared-stack'
import { formatDistanceToNowStrict } from 'date-fns'
import { navigate } from 'gatsby'
import { UserRoutes } from '~config/routes'
import PatientStatusTag from '~components/shared/patient/status-tag'

interface Props {
    chat?: ChatFieldsFragment
    patient: PatientFieldsFragment
    size?: 'sm' | 'md'
}

export default function ChatClientDetailsPatientItem({
    chat,
    patient,
    size = 'sm',
}: Props) {
    const isSmall = size === 'sm'

    return (
        <SharedStack
            row
            p={isSmall ? 2 : 3}
            rounded={isSmall ? 'xl' : '2xl'}
            _hover={{ bg: 'gray.50' }}
            cursor="pointer"
            onClick={() => {
                if (chat?.id) {
                    navigate(
                        UserRoutes.ChatConversation(chat.id) +
                            `&patient=${patient.id}`,
                    )
                } else {
                    navigate(UserRoutes.PatientDetails(patient.id))
                }
            }}
        >
            <SpeciesIcon patient={patient} size={isSmall ? 9 : 10} />
            <Box flex={1}>
                <SharedStack row spaced>
                    <Text fontSize={isSmall ? 'xs' : 'sm'} fontWeight="medium">
                        {patient.name}
                    </Text>
                    <PatientStatusTag
                        patient={patient}
                        size={isSmall ? 'xs' : 'sm'}
                    />
                </SharedStack>
                <SharedStack
                    row
                    flexWrap="wrap"
                    columnGap={1}
                    rowGap={0}
                    divider={
                        <span style={{ lineHeight: '16px', color: 'gray.500' }}>
                            &middot;
                        </span>
                    }
                >
                    {getPatientInfo(patient).map(pi => (
                        <Text
                            key={pi}
                            fontSize={isSmall ? '11px' : '12px'}
                            color="gray.500"
                        >
                            {pi}
                        </Text>
                    ))}
                </SharedStack>
            </Box>
        </SharedStack>
    )
}

function getPatientInfo(patient: PatientFieldsFragment): string[] {
    const info: (string | null | undefined)[] = []

    if (patient.clinic_breed?.description || patient.breed) {
        info.push(patient.clinic_breed?.description || patient.breed)
    }

    if (patient.clinic_species?.species) {
        info.push(patient.clinic_species?.species)
    }

    if (patient.gender) {
        info.push(patient.gender)
    }

    if (patient.birthday) {
        info.push(formatDistanceToNowStrict(patient.birthday))
    }

    return info.filter(Boolean) as string[]
}
